import { useEffect, useRef, useState } from "react";
import type { JSONContent } from "@tiptap/core";
import { useTranslation } from "react-i18next";
import { LoreEditor } from "@/editors/rich-text/lore-editor";
import { cloneEmptyStoryDocument, type MainStoryView } from "@/features/story";
import type { StoryEditorSaveState } from "@/components/story/story-workspace-header";

const SAVE_DELAY_MS = 800;

type MainStoryDocumentEditorProps = {
  onSave: (content: JSONContent) => Promise<void>;
  onSaveStateChange?: (
    state: StoryEditorSaveState,
    error: string | null,
  ) => void;
  story: MainStoryView;
};

export function MainStoryDocumentEditor({
  onSave,
  onSaveStateChange,
  story,
}: MainStoryDocumentEditorProps) {
  const { t } = useTranslation();
  const [content, setContent] = useState<JSONContent>(
    story.body ?? cloneEmptyStoryDocument(),
  );
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<StoryEditorSaveState>("idle");
  const onSaveRef = useRef(onSave);
  const storyBodyRef = useRef(story.body);

  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  useEffect(() => {
    storyBodyRef.current = story.body;
  }, [story.body]);

  useEffect(() => {
    setContent(storyBodyRef.current ?? cloneEmptyStoryDocument());
    setSaveError(null);
    setSaveState("idle");
  }, [story.id]);

  useEffect(() => {
    onSaveStateChange?.(saveState, saveError);
  }, [onSaveStateChange, saveError, saveState]);

  useEffect(() => {
    if (JSON.stringify(content) === JSON.stringify(story.body ?? cloneEmptyStoryDocument())) {
      return;
    }

    setSaveState("saving");
    const timeout = window.setTimeout(() => {
      void (async () => {
        try {
          await onSaveRef.current(content);
          setSaveError(null);
          setSaveState("saved");
        } catch (error) {
          setSaveError(error instanceof Error ? error.message : t("story.messages.saveError"));
          setSaveState("error");
        }
      })();
    }, SAVE_DELAY_MS);

    return () => window.clearTimeout(timeout);
  }, [content, story.body, t]);

  return (
    <LoreEditor
      autofocus
      content={content}
      contentClassName="lore-editor-page-breaks"
      onChange={setContent}
      paginated={false}
      placeholder={t("story.editors.mainPlaceholder")}
    />
  );
}
